import AuthorityManagementMixin from "@/shared/mixins/authorityManagement/authorityManagementMixin";
import Rules from "@/shared/validators/formRules";
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "InvoicingRules",

  data: () => ({
    compRules: null,
    moment,
    isHelpdesk: false,
  }),

  mixins: [AuthorityManagementMixin],

  methods: {
    getInvoicesToHrProtected(invoices) {
      return invoices.map((invoice) => {
        return {
          id: invoice.id,
          contract: `${invoice.contractNumber} ${invoice.contractNumber}`,
          subcontract: `${invoice.subContractNumber} ${invoice.subContractNumber}`,
          cnpj: invoice.cnpj,
          carrierName: invoice.carrierName,
          benefitType: invoice.benefitType,
          invoiceType: invoice.invoiceType,
          competencyDate: this.formatter.formatDateTimeZoneMonthAndYear(invoice.competencyDate),
          limitDate: this.formatter.formatDateTimeZone(invoice.limitDate),
          value: this.formatter.formatToBRLCurrency(invoice.value),
          numberLives: invoice.numberLives,
          downloads: invoice.downloads,
        };
      });
    },
    getInvoicesToXippPlatform(invoices) {
      return invoices.map((invoice) => {
        return {
          ...invoice,
          contract: `${invoice.contractNumber} ${invoice.contractNumber}`,
          subcontract: `${invoice.subContractNumber} ${invoice.subContractNumber}`,
          financialGroup: "Grgupo financeiro",
          competencyDate: this.formatter.formatDateTimeZoneMonthAndYear(invoice.competencyDate),
          value: this.formatter.formatToBRLCurrency(invoice.value),
          bankSlipValue: this.formatter.formatToBRLCurrency(invoice.bankSlipValue),
          limitDate: this.formatter.formatDateTimeZone(invoice.limitDate),
          copayValue: this.formatter.formatToBRLCurrency(invoice.copayValue),
          premiumValue: this.formatter.formatToBRLCurrency(invoice.premiumValue),
          iofValue: this.formatter.formatToBRLCurrency(invoice.iofValue),
          settlementValue: this.formatter.formatToBRLCurrency(invoice.settlementValue),
          otherAmountsValue: this.formatter.formatToBRLCurrency(invoice.otherAmountsValue),
          replaceFeeValue: this.formatter.formatToBRLCurrency(invoice.replaceFeeValue),
        }
      })
    },
  },

  computed: {
    ...mapGetters({
      invoicingFilter: "invoicing/invoicingFilter",
      invoicingStatus: "invoicing/invoicingStatus",
      isRHProtegido: "user-info/isRHProtegido",
      invoices: "invoicing/invoices",
    }),

    /**
     * Exibe o componente de status apenas no BACKOFFICE.
     *
     * Regra de negócio: só mostrar o componente de for BACKOFFICE.
     *
     * @returns {boolean} Verdadeiro se for RH Protegido (não mostrar).
     */
    showSubFilterComponent() {
      return !this.isRHProtegido;
    },

    /**
     * Exibe o filtro protocolo apenas para o BACKOFFICE.
     *
     * Regra de negócio: só mostrar o filtro protocolo se for BACKOFFICE.
     *                   além da regra showProtocolFilter
     *
     * @returns {boolean} Verdadeiro se for RH Protegido (não mostrar).
     */
    showProtocolFilter() {
      return this.contractWasSelected && !this.isRHProtegido;
    },

    /**
     * Exibe o filtro de protocolo apenas quando o campo de contrato foi selecionado.
     *
     * Regra de negócio: Apresentar o filtro de protocolo se houver um `contractId` no filtro atual.
     *
     * @returns {boolean} Verdadeiro se o contrato foi selecionado.
     */
    contractWasSelected() {
      return this.invoicingFilter.contractId ? true : false;
    },

    /**
     * Regra 1 dos campos Data Limite e Permanência Indeterminada.
     *
     * Regra de negócio:
     *    Caso Motivo de Permanência seja Filho(a) Universitário(a):
     *      Obrigatório o preenchimento do campo Data Limite.
     *
     * @returns {boolean} Verdadeiro se regra acima for cumprida.
     */
    validateCollegeSonRules() {
      // adicionar regra de data
      return this.requestForm.coverageExtensionReason && this.requestForm.coverageExtensionLimitDate ? true : false;
    },

    /**
     * Regra 2 dos campos Data Limite e Permanência Indeterminada.
     *
     * Regra de negócio:
     *    Caso Motivo de Permanência seja Pessoa com Deficiência ou Condição Comercial:
     *      Obrigatório o preenchimento de Data Limite ou Permanência Indeterminada.
     *
     * @returns {boolean} Verdadeiro se regra acima for cumprida.
     */
    validatePersonWithDisabilityOrCommercialConditionRules() {
      if (this.requestForm.coverageExtensionReason) {
        return this.requestForm.isCoverageLimitIndefinite || this.requestForm.coverageExtensionLimitDate;
      }

      return false;
    },

    limitDateRules() {
      const required = this.compRules.required(this.requestForm.coverageExtensionLimitDate);
      const requiredDate = this.compRules.requiredDate(this.requestForm.coverageExtensionLimitDate);
      const isAfterDateNow = this.compRules.isAfterDateNow(this.requestForm.coverageExtensionLimitDate);

      return [required, requiredDate, isAfterDateNow];
    },

    endDateRules() {
      const required = this.compRules.required(this.requestForm.endDate);
      const requiredDate = this.compRules.requiredDate(this.requestForm.endDate);
      const isDateValid = this.compRules.isDateValid(this.requestForm.endDate);
      const isAfterDateNow = this.compRules.isAfterDateNow(this.requestForm.endDate);

      return [isDateValid, required, requiredDate, isAfterDateNow];
    },

    isLimitDateValid() {
      const isDateValid = this.compRules.isDateValid(this.requestForm.coverageExtensionLimitDate);
      const isAfterDateNow = this.compRules.isAfterDateNow(this.requestForm.coverageExtensionLimitDate);
      return [isDateValid, isAfterDateNow];
    },

    reprocessButtonRules() {
      let rules = {
        disabled: true,
        show: true,
      };

      if (this.isRHProtegido) rules.show = false;
      if (!this.hasPermission("sdi_maioridade_acao_reprocessar")) rules.show = false;
      if (this.invoicingFilter.financialGroupId && this.invoicingFilter.contractId) rules.disabled = false;

      if (!this.isReprocessable) rules.disabled = true;

      return rules;
    },

    isReprocessable() {
      return this.hasInvoices && this.invoices.some((invoicing) => invoicing.isReprocessable);
    },

    hasInvoices() {
      return this.invoices && this.invoices.length > 0;
    },

    isCurrentCompetence() {
      const isCurrentMonth = parseInt(moment().format("MM")) === this.invoicingFilter.competenceMonth;
      const isCurrentYear = parseInt(moment().format("YYYY")) === this.invoicingFilter.competenceYear;

      return isCurrentMonth && isCurrentYear;
    },

    exportButtonRules() {
      let rules = {
        disabled: true,
        show: true,
      };

      if (this.invoicingFilter.financialGroupId) rules.disabled = false;

      if (!this.hasPermission("sdi_invoicing_export")) {
        rules.show = false;
      }

      return rules;
    },
  },

  created() {
    this.compRules = new Rules();
  },
};
